/**
 * OTL Storefront Agent — loader.
 *
 * Injects an iframe pointing at the OTL Custom OS embed route. Tiny on
 * purpose — all UI/AI logic lives server-side in the OS, not in this script.
 *
 * Lifecycle:
 *   1. Check kill switch via /api/storefront-agent/health. If disabled, exit.
 *   2. Generate / read visitorKey from localStorage.
 *   3. Read window.OTL_AGENT_CUSTOMER (set by the snippet from Liquid) when
 *      the visitor is signed in.
 *   4. Build launcher bubble + iframe (hidden until clicked).
 *   5. Bridge cart actions: iframe asks parent to add to Shopify cart; we
 *      hit /cart/add.js on this origin and post the result back.
 */
(function () {
  'use strict';
  if (window.__otlAgentLoaded) return;
  window.__otlAgentLoaded = true;

  var OS_ORIGIN = window.OTL_AGENT_OS_ORIGIN || 'https://otl-custom-os-v2.vercel.app';
  var EMBED_PATH = '/embed/storefront-agent';
  var HEALTH_PATH = '/api/storefront-agent/health';
  var VISITOR_KEY_LS = 'otl_agent_visitor_key';

  function uuidv4() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
  function getVisitorKey() {
    try {
      var k = localStorage.getItem(VISITOR_KEY_LS);
      if (k && /^[0-9a-f-]{36}$/i.test(k)) return k;
      k = uuidv4();
      localStorage.setItem(VISITOR_KEY_LS, k);
      return k;
    } catch (_e) {
      return uuidv4();
    }
  }

  function checkHealth() {
    return fetch(OS_ORIGIN + HEALTH_PATH, { credentials: 'omit', mode: 'cors' })
      .then(function (r) { return r.ok ? r.json() : { enabled: false }; })
      .catch(function () { return { enabled: false }; });
  }

  // Resolve a Shopify variant GID (gid://shopify/ProductVariant/12345) → numeric ID
  function gidToNumericVariantId(gid) {
    if (typeof gid !== 'string') return null;
    var m = gid.match(/ProductVariant\/(\d+)/);
    return m ? m[1] : null;
  }

  // Add to Shopify cart on this storefront origin. Uses the AJAX cart API
  // (https://shopify.dev/docs/api/ajax/reference/cart). Same-origin fetch
  // so the cart cookie is included automatically.
  function shopifyCartAdd(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ id: Number(variantId), quantity: Number(quantity) || 1 }),
    }).then(function (r) {
      return r.json().then(function (body) { return { ok: r.ok, status: r.status, body: body }; });
    });
  }

  // After a successful add, get cart count for the badge
  function shopifyCartCount() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) { return cart && typeof cart.item_count === 'number' ? cart.item_count : null; })
      .catch(function () { return null; });
  }

  // Tell the theme that the cart changed, so any cart-drawer or count badges
  // can refresh. Most themes listen for these events; harmless if none do.
  function broadcastCartChange(cart) {
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cart, bubbles: true }));
      document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      // Retina-specific (this theme's flavor)
      if (window.theme && window.theme.cartDrawer && typeof window.theme.cartDrawer.refresh === 'function') {
        try { window.theme.cartDrawer.refresh(); } catch (_e) {}
      }
    } catch (_e) {}
  }

  var STYLE = '\
#otl-agent-launcher{position:fixed;bottom:20px;right:20px;z-index:2147483640;width:56px;height:56px;border-radius:50%;background:#1a1a1a;color:#fff;border:0;cursor:pointer;box-shadow:0 8px 24px rgba(0,0,0,.2);display:flex;align-items:center;justify-content:center;transition:transform .15s ease}\
#otl-agent-launcher:hover{transform:scale(1.06)}\
#otl-agent-launcher svg{width:24px;height:24px}\
#otl-agent-launcher .otl-cart-badge{position:absolute;top:-4px;right:-4px;background:#dc2626;color:#fff;font-size:10px;font-weight:600;border-radius:999px;padding:2px 6px;min-width:18px;text-align:center;display:none;font-family:system-ui,-apple-system,sans-serif}\
#otl-agent-launcher .otl-cart-badge.show{display:block}\
#otl-agent-launcher.otl-pulse::before,#otl-agent-launcher.otl-pulse::after{content:"";position:absolute;inset:0;border-radius:50%;background:#1a1a1a;opacity:.4;z-index:-1;animation:otlPulse 2s ease-out infinite}\
#otl-agent-launcher.otl-pulse::after{animation-delay:1s}\
@keyframes otlPulse{0%{transform:scale(1);opacity:.4}100%{transform:scale(1.8);opacity:0}}\
#otl-agent-tip{position:fixed;bottom:90px;right:20px;z-index:2147483639;background:#1a1a1a;color:#fff;padding:10px 14px;border-radius:12px;font-family:system-ui,-apple-system,sans-serif;font-size:13px;line-height:1.35;max-width:240px;box-shadow:0 8px 24px rgba(0,0,0,.2);opacity:0;transform:translateY(8px);pointer-events:auto;transition:opacity .25s ease,transform .25s ease;cursor:pointer}\
#otl-agent-tip.show{opacity:1;transform:translateY(0)}\
#otl-agent-tip::after{content:"";position:absolute;bottom:-6px;right:24px;width:12px;height:12px;background:#1a1a1a;transform:rotate(45deg)}\
#otl-agent-tip .otl-tip-close{position:absolute;top:4px;right:6px;width:18px;height:18px;background:transparent;color:rgba(255,255,255,.65);border:0;border-radius:50%;font-size:14px;cursor:pointer;padding:0;line-height:1}\
#otl-agent-tip .otl-tip-close:hover{color:#fff;background:rgba(255,255,255,.15)}\
#otl-agent-frame{position:fixed;bottom:90px;right:20px;z-index:2147483641;width:380px;height:560px;max-width:calc(100vw - 40px);max-height:calc(100vh - 120px);border:0;border-radius:14px;background:transparent;display:none;color-scheme:light}\
#otl-agent-frame.is-open{display:block}\
@media (max-width:480px){#otl-agent-frame{width:calc(100vw - 24px);height:calc(100vh - 110px);bottom:80px;right:12px}#otl-agent-tip{right:12px;max-width:200px}}';

  function mount() {
    var style = document.createElement('style');
    style.id = 'otl-agent-style';
    style.textContent = STYLE;
    document.head.appendChild(style);

    var btn = document.createElement('button');
    btn.id = 'otl-agent-launcher';
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Open chat');
    btn.innerHTML = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg><span class="otl-cart-badge" aria-hidden="true"></span>';
    var badge = btn.querySelector('.otl-cart-badge');

    var iframe = document.createElement('iframe');
    iframe.id = 'otl-agent-frame';
    iframe.title = 'OTL Apparel chat';
    iframe.setAttribute('allow', 'clipboard-write');
    iframe.setAttribute('referrerpolicy', 'no-referrer');
    var visitorKey = getVisitorKey();
    var qs = '?v=' + encodeURIComponent(visitorKey);
    iframe.src = OS_ORIGIN + EMBED_PATH + qs;

    btn.addEventListener('click', function () {
      iframe.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', iframe.classList.contains('is-open') ? 'true' : 'false');
      // Hide the badge once they open the chat
      if (badge) { badge.classList.remove('show'); badge.textContent = ''; }
      // Stop pulsing + dismiss tooltip the moment they engage
      stopAttention();
    });

    document.body.appendChild(iframe);
    document.body.appendChild(btn);

    // First-visit attention — pulse the bubble + show "Try me out" tooltip
    // until the visitor clicks it (or dismisses) once. Stored in localStorage
    // so we never nag a returning visitor.
    var SEEN_KEY = 'otl_agent_attention_seen_v1';
    var tip = null;
    var alreadySeen = false;
    try { alreadySeen = !!localStorage.getItem(SEEN_KEY); } catch (_e) {}

    function stopAttention() {
      btn.classList.remove('otl-pulse');
      if (tip) { tip.remove(); tip = null; }
      try { localStorage.setItem(SEEN_KEY, '1'); } catch (_e) {}
    }

    function startAttention() {
      if (alreadySeen) return;
      // Pulse animation on the launcher
      btn.classList.add('otl-pulse');
      // Tooltip a moment later so it lands after the page settles
      setTimeout(function () {
        if (alreadySeen) return;
        tip = document.createElement('div');
        tip.id = 'otl-agent-tip';
        tip.innerHTML = '<button class="otl-tip-close" type="button" aria-label="Dismiss">×</button>Try me — ask me anything about our blanks, sizes, or custom work.';
        tip.querySelector('.otl-tip-close').addEventListener('click', function (e) {
          e.stopPropagation();
          stopAttention();
        });
        tip.addEventListener('click', function () {
          // Tapping the tooltip itself opens the chat
          if (!iframe.classList.contains('is-open')) btn.click();
        });
        document.body.appendChild(tip);
        // Give the browser a tick so the transition runs
        setTimeout(function () { if (tip) tip.classList.add('show'); }, 30);
      }, 1200);
      // Auto-fade the tooltip after a while even if they don't interact —
      // keep the bubble pulsing until they click.
      setTimeout(function () {
        if (tip) { tip.remove(); tip = null; }
      }, 18000);
    }
    startAttention();

    // Send init payload — visitor key, customer (if logged in), page context
    function sendInit() {
      var customer = window.OTL_AGENT_CUSTOMER && typeof window.OTL_AGENT_CUSTOMER === 'object'
        ? window.OTL_AGENT_CUSTOMER : null;
      try {
        iframe.contentWindow.postMessage({
          target: 'otl-storefront-agent',
          type: 'init',
          visitorKey: visitorKey,
          customer: customer,
          pageUrl: window.location.href,
          pageTitle: document.title,
        }, OS_ORIGIN);
      } catch (_e) {}
    }

    // Listen for messages from iframe
    window.addEventListener('message', function (ev) {
      if (ev.origin !== OS_ORIGIN) return;
      if (!ev.data || ev.data.source !== 'otl-storefront-agent') return;

      if (ev.data.type === 'ready') {
        sendInit();
        return;
      }

      // Iframe asks us to collapse the panel so the Shopify cart drawer
      // (which opens automatically on add) isn't blocked. Launcher stays.
      if (ev.data.type === 'minimize') {
        iframe.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
        return;
      }

      // Click on a product card image/title — navigate the parent to the PDP.
      if (ev.data.type === 'navigate' && typeof ev.data.url === 'string') {
        // Only allow same-origin navigation
        try {
          var u = new URL(ev.data.url, window.location.origin);
          if (u.origin === window.location.origin) {
            window.location.href = u.href;
          }
        } catch (_e) {}
        return;
      }

      if (ev.data.type === 'cart_add') {
        var requestId = ev.data.requestId;
        var variantId = gidToNumericVariantId(ev.data.variantId);
        var quantity = Math.max(1, Math.min(999, parseInt(ev.data.quantity, 10) || 1));
        if (!variantId) {
          try { iframe.contentWindow.postMessage({ target:'otl-storefront-agent', type:'cart_result', requestId: requestId, ok:false, error:'invalid_variant' }, OS_ORIGIN); } catch (_e) {}
          return;
        }
        shopifyCartAdd(variantId, quantity).then(function (res) {
          var ok = res && res.ok;
          if (ok) {
            shopifyCartCount().then(function (count) {
              if (badge && count != null) {
                badge.textContent = String(count);
                badge.classList.add('show');
              }
              try {
                iframe.contentWindow.postMessage({
                  target: 'otl-storefront-agent', type: 'cart_result',
                  requestId: requestId, ok: true,
                  totalQty: count, lineItem: res.body && { title: res.body.product_title, qty: res.body.quantity, price: res.body.price },
                }, OS_ORIGIN);
              } catch (_e) {}
              broadcastCartChange(res.body);
            });
          } else {
            try {
              iframe.contentWindow.postMessage({
                target: 'otl-storefront-agent', type: 'cart_result',
                requestId: requestId, ok: false,
                error: (res && res.body && (res.body.description || res.body.message)) || 'cart_add_failed',
              }, OS_ORIGIN);
            } catch (_e) {}
          }
        }).catch(function (e) {
          try {
            iframe.contentWindow.postMessage({
              target: 'otl-storefront-agent', type: 'cart_result',
              requestId: requestId, ok: false,
              error: 'network',
            }, OS_ORIGIN);
          } catch (_e) {}
        });
      }
    });
  }

  function start() {
    checkHealth().then(function (h) {
      if (!h || !h.enabled) return;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', mount, { once: true });
      } else {
        mount();
      }
    });
  }

  start();
})();
